import { useState, useEffect, useCallback, ReactNode } from 'react';
import { PortalLayout } from '../components/PortalLayout';
import { api } from '../api';

/* ── Types ─────────────────────────────────────────────────────────── */

interface JobSummary {
  id: number; job_number: string; status: string; job_date: string | null;
  job_end_date: string | null; start_time: string | null;
  customer: { id: number; customer_name: string } | null;
  assigned_driver_id: number | null; driver_name: string | null;
  assigned_vehicle_id: number | null; vehicle_reg: string | null;
  total_price: number | null; total_distance_miles: number | null;
  po_number: string | null; notes: string | null; quote_id: number | null;
  dispatch_date: string | null; completed_at: string | null;
  invoiced_at: string | null; created_at: string | null;
  total_driving_hours: number | null; total_duty_hours: number | null;
  overnight_count: number | null;
}

interface LoadItem {
  id?: number; item_order: number; description: string; quantity: number;
  weight_kg: number | null; length_cm: number | null; width_cm: number | null;
  height_cm: number | null; is_fragile: boolean; is_hazardous: boolean;
  hazard_class: string | null; notes: string | null;
}

interface JobDetail extends JobSummary {
  client_contact_name: string | null; client_contact_phone: string | null;
  client_contact_email: string | null;
  collection_address: string | null; collection_postcode: string | null;
  collection_instructions: string | null;
  delivery_address: string | null; delivery_postcode: string | null;
  delivery_instructions: string | null;
  load_description: string | null; total_weight_kg: number | null;
  special_requirements: string | null;
  driver_phone: string | null; driver_email: string | null;
  vehicle: { id: number; registration: string; make: string | null; model: string | null } | null;
  assigned_at: string | null;
  journey_plan: any; route_geometry: number[][] | null; stops: any[] | null;
  load_items: LoadItem[];
  assignment_warnings?: string[];
  job_type?: string; /* haulage|coach_psv */
  is_sub_contracted?: boolean;
  sub_contractor_id?: number | null;
  sub_contractor_name?: string | null;
  sub_contractor_contact?: string | null;
  sub_contractor_phone?: string | null;
  sub_contractor_agreed_price?: number | null;
  sub_contractor_po?: string | null;
  passenger_count?: number | null;
  vehicle_class?: string | null; /* Minicoach|Midi|Standard|Executive|Double Decker|Minibus */
  wheelchair_accessible?: boolean;
  low_floor?: boolean;
  wheelchair_positions?: number | null;
  pickup_schedule?: Array<{ location: string; time: string }> | null;
  dropoff_schedule?: Array<{ location: string; time: string }> | null;
  /* Costing fields */
  quoted_price?: number | null;
  agreed_price?: number | null;
  additional_charges?: number | null;
  fuel_cost?: number | null;
  fuel_rate_per_mile?: number | null;
  driver_wages_hours?: number | null;
  driver_rate_per_hour?: number | null;
  vehicle_running_cost?: number | null;
  vehicle_rate_per_mile?: number | null;
  tolls_charges?: number | null;
  parking_congestion?: number | null;
  overnight_allowance_rate?: number | null;
  sub_contractor_cost?: number | null;
  other_costs?: string | null;
  other_costs_amount?: number | null;
}

interface AvailResource {
  id: number; name?: string; registration?: string; make?: string; model?: string;
  phone?: string; email?: string; depot_id: number | null;
  is_available: boolean; conflicts: { type: string; detail: string }[];
}

/* ── Constants ─────────────────────────────────────────────────────── */

const STAGES = [
  { key: 'awaiting_assignment', label: 'Awaiting', short: 'Awaiting' },
  { key: 'booked_in', label: 'Booked In', short: 'Booked' },
  { key: 'assigned', label: 'Assigned', short: 'Assigned' },
  { key: 'dispatched', label: 'Dispatched', short: 'Dispatched' },
  { key: 'in_progress', label: 'In Progress', short: 'Active' },
  { key: 'completed', label: 'Completed', short: 'Done' },
  { key: 'invoiced', label: 'Invoiced', short: 'Invoiced' },
];

// Map legacy status values to the canonical pipeline key
function normaliseStatus(s: string): string {
  if (s === 'scheduled') return 'awaiting_assignment';
  return s;
}

const STATUS_LABELS: Record<string, string> = {
  awaiting_assignment: 'Awaiting Assignment', scheduled: 'Awaiting Assignment',
  booked_in: 'Booked In', assigned: 'Assigned', dispatched: 'Dispatched',
  in_progress: 'In Progress', completed: 'Completed', invoiced: 'Invoiced',
  cancelled: 'Cancelled',
};

const STATUS_BG: Record<string, string> = {
  awaiting_assignment: 'bg-amber-500', booked_in: 'bg-blue-500',
  assigned: 'bg-indigo-500', dispatched: 'bg-purple-500',
  in_progress: 'bg-green-500', completed: 'bg-gray-500',
  invoiced: 'bg-emerald-500', cancelled: 'bg-red-500',
};

const STATUS_PILL: Record<string, string> = {
  awaiting_assignment: 'bg-amber-100 text-amber-800',
  booked_in: 'bg-blue-100 text-blue-800',
  assigned: 'bg-indigo-100 text-indigo-800',
  dispatched: 'bg-purple-100 text-purple-800',
  in_progress: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-800',
  invoiced: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
};


/* ══════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════════════════════════ */

export function JobsPage() {
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [stats, setStats] = useState<Record<string, number>>({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedJob, setSelectedJob] = useState<JobDetail | null>(null);

  const loadJobs = useCallback(async () => {
    try {
      setLoading(true);
      const [res, statsRes] = await Promise.all([
        api.getJobs({ status: statusFilter !== 'all' ? statusFilter : undefined, search: search || undefined, page }),
        api.getJobStats(),
      ]);
      setJobs(res.items || []);
      setTotal(res.total || 0);
      setStats(statsRes);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [statusFilter, search, page]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const openJob = async (id: number) => {
    try {
      const detail = await api.getJob(id);
      setSelectedJob(detail);
    } catch (e: any) { setError(e.message); }
  };

  const refreshJob = async () => {
    if (!selectedJob) return;
    try {
      const detail = await api.getJob(selectedJob.id);
      setSelectedJob(detail);
    } catch (e: any) { setError(e.message); }
  };

  const closeJob = () => { setSelectedJob(null); loadJobs(); };

  return (
    <PortalLayout currentPage="jobs">
      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 text-red-700 rounded text-sm flex justify-between items-center">
          <span>{error}</span>
          <button className="text-red-500 hover:text-red-700 text-xs font-medium" onClick={() => setError('')}>Dismiss</button>
        </div>
      )}

      {selectedJob ? (
        <JobPlanner job={selectedJob} onClose={closeJob} onRefresh={refreshJob} setError={setError} />
      ) : (
        <JobBoard
          jobs={jobs} total={total} page={page} stats={stats}
          statusFilter={statusFilter} search={search} loading={loading}
          onSearch={setSearch} onFilter={s => { setStatusFilter(s); setPage(1); }}
          onPage={setPage} onSelect={openJob}
        />
      )}
    </PortalLayout>
  );
}


/* ══════════════════════════════════════════════════════════════════════
   JOB BOARD — Kanban-style list with status grouping
   ══════════════════════════════════════════════════════════════════════ */

function JobBoard({
  jobs, total, page, stats, statusFilter, search, loading,
  onSearch, onFilter, onPage, onSelect,
}: {
  jobs: JobSummary[]; total: number; page: number; stats: Record<string, number>;
  statusFilter: string; search: string; loading: boolean;
  onSearch: (s: string) => void; onFilter: (s: string) => void;
  onPage: (p: number) => void; onSelect: (id: number) => void;
}) {
  const totalAll = Object.values(stats).reduce((a, b) => a + b, 0);
  const activeCount = Object.entries(stats)
    .filter(([s]) => !['completed', 'invoiced', 'cancelled'].includes(s))
    .reduce((sum, [, c]) => sum + c, 0);

  return (
    <div className="p-5">
      {/* Header bar */}
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="text-sm font-semibold uppercase tracking-wide text-gray-900">Job Planner</h1>
          <p className="text-sm text-gray-500 mt-0.5">{activeCount} active · {totalAll} total</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="text" placeholder="Search jobs, PO, postcode..."
            value={search} onChange={e => onSearch(e.target.value)}
            className="w-72 px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
          />
          <button
            className="px-4 py-2 bg-slate-700 text-white text-sm font-semibold rounded hover:bg-slate-800 transition whitespace-nowrap">
            + New Job
          </button>
        </div>
      </div>

      {/* Pipeline summary row */}
      <div className="grid grid-cols-8 gap-2 mb-5">
        <PipeBtn active={statusFilter === 'all'} onClick={() => onFilter('all')}
          label="All" count={totalAll} color="bg-gray-600" />
        {STAGES.map(s => (
          <PipeBtn key={s.key} active={statusFilter === s.key} onClick={() => onFilter(s.key)}
            label={s.short} count={stats[s.key] || 0} color={STATUS_BG[s.key]} />
        ))}
      </div>

      {/* Jobs list */}
      {loading ? (
        <div className="text-center py-20 text-gray-400">Loading...</div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-gray-300 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <p className="text-gray-400 text-sm">No jobs found</p>
          <p className="text-gray-300 text-xs mt-1">Jobs are created when quotes are approved</p>
        </div>
      ) : (
        <div className="space-y-2">
          {jobs.map(j => (
            <div key={j.id} onClick={() => onSelect(j.id)}
              className="bg-white rounded border border-gray-200 hover:border-slate-300 hover:shadow-md transition-all cursor-pointer px-5 py-4 flex items-center gap-5">
              {/* Status dot */}
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_BG[j.status] || 'bg-gray-400'}`} />

              {/* Job number + status */}
              <div className="w-36 flex-shrink-0">
                <p className="text-sm font-bold text-gray-900">{j.job_number}</p>
                <span className={`inline-flex mt-0.5 px-2 py-0.5 text-[10px] font-semibold rounded ${STATUS_PILL[j.status] || 'bg-gray-100 text-gray-600'}`}>
                  {STATUS_LABELS[j.status] || j.status}
                </span>
              </div>

              {/* Date */}
              <div className="w-28 flex-shrink-0">
                <p className="text-xs text-gray-400">Date</p>
                <p className="text-sm text-gray-700 font-medium">
                  {j.job_date || <span className="text-gray-300 italic">Not set</span>}
                </p>
                {j.start_time && <p className="text-[10px] text-gray-400">{j.start_time}</p>}
              </div>

              {/* Customer */}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400">Customer</p>
                <p className="text-sm text-gray-800 font-medium truncate">
                  {j.customer?.customer_name || <span className="text-gray-300">—</span>}
                </p>
              </div>

              {/* Route */}
              <div className="w-28 flex-shrink-0 text-right">
                {j.total_distance_miles ? (
                  <>
                    <p className="text-sm font-semibold text-gray-800">{j.total_distance_miles.toFixed(0)} mi</p>
                    {j.total_driving_hours && <p className="text-[10px] text-gray-400">{j.total_driving_hours.toFixed(1)}h drive</p>}
                  </>
                ) : <p className="text-sm text-gray-300">—</p>}
              </div>

              {/* Driver / Vehicle */}
              <div className="w-40 flex-shrink-0">
                {j.driver_name ? (
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                      {j.driver_name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-700 font-medium truncate">{j.driver_name}</p>
                      <p className="text-[10px] text-gray-400 truncate">{j.vehicle_reg || '—'}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-300 italic">Unassigned</p>
                )}
              </div>

              {/* Price */}
              <div className="w-24 flex-shrink-0 text-right">
                <p className="text-sm font-bold text-gray-900">
                  {j.total_price != null ? `£${j.total_price.toFixed(0)}` : '—'}
                </p>
              </div>

              {/* Chevron */}
              <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 25 && (
        <div className="flex justify-between items-center mt-4 text-sm text-gray-500">
          <span>Page {page} of {Math.ceil(total / 25)}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => onPage(page - 1)}
              className="px-3 py-1.5 border rounded text-sm disabled:opacity-30 hover:bg-gray-50">Prev</button>
            <button disabled={page >= Math.ceil(total / 25)} onClick={() => onPage(page + 1)}
              className="px-3 py-1.5 border rounded text-sm disabled:opacity-30 hover:bg-gray-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PipeBtn({ active, onClick, label, count, color }: {
  active: boolean; onClick: () => void; label: string; count: number; color: string;
}) {
  return (
    <button onClick={onClick}
      className={`rounded p-3 text-center transition-all ${
        active ? 'bg-white shadow-md border-2 border-slate-500 scale-[1.02]' : 'bg-white border border-gray-200 hover:shadow'
      }`}>
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <div className={`w-2 h-2 rounded-full ${color}`} />
        <span className="text-xs font-medium text-gray-600">{label}</span>
      </div>
      <p className="text-sm font-semibold text-gray-900">{count}</p>
    </button>
  );
}


/* ══════════════════════════════════════════════════════════════════════
   JOB PLANNER — the main interactive job management view
   ══════════════════════════════════════════════════════════════════════ */

function JobPlanner({
  job, onClose, onRefresh, setError,
}: {
  job: JobDetail; onClose: () => void; onRefresh: () => Promise<void>;
  setError: (s: string) => void;
}) {
  const status = normaliseStatus(job.status);

  const [activeTab, setActiveTab] = useState<string>(() => {
    // Auto-select the most useful tab based on status
    if (status === 'awaiting_assignment') return 'booking';
    if (status === 'booked_in') return 'resources';
    if (status === 'assigned' || status === 'dispatched') return 'briefing';
    return 'overview';
  });
  const [busy, setBusy] = useState('');

  const doStatus = async (newStatus: string) => {
    try {
      setBusy(newStatus);
      await api.updateJobStatus(job.id, { status: newStatus });
      await onRefresh();
    } catch (e: any) { setError(e.message); }
    finally { setBusy(''); }
  };

  // Work out what the next natural action is
  const stageIdx = STAGES.findIndex(s => s.key === status);
  const nextStage = stageIdx >= 0 && stageIdx < STAGES.length - 1 ? STAGES[stageIdx + 1] : null;

  const TABS = [
    { key: 'overview', label: 'Overview', icon: overviewIcon },
    { key: 'booking', label: 'Booking', icon: bookingIcon },
    { key: 'resources', label: 'Resources', icon: resourcesIcon },
    { key: 'costing', label: 'Costing', icon: costingIcon },
    { key: 'briefing', label: 'Work Ticket', icon: briefingIcon },
    { key: 'timeline', label: 'Journey', icon: journeyIcon },
    { key: 'activity', label: 'Activity', icon: activityIcon },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-0px)]">
      {/* ─── Top bar ──────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-sm font-semibold uppercase text-gray-900">{job.job_number}</h1>
              <span className={`px-2.5 py-0.5 text-[11px] font-semibold rounded ${STATUS_PILL[status] || 'bg-gray-100 text-gray-600'}`}>
                {STATUS_LABELS[status] || status}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              {job.customer?.customer_name || 'No customer'}
              {job.po_number && <span className="ml-2">PO: {job.po_number}</span>}
              {job.total_price != null && <span className="ml-2 font-medium text-gray-600">£{job.total_price.toFixed(2)}</span>}
            </p>
          </div>
        </div>

        {/* Status actions */}
        <div className="flex items-center gap-2">
          {nextStage && nextStage.key !== 'cancelled' && !['completed', 'invoiced'].includes(status) && (
            <button onClick={() => doStatus(nextStage.key)} disabled={!!busy}
              className={`px-4 py-2 text-sm font-semibold rounded text-white transition ${STATUS_BG[nextStage.key]} hover:opacity-90 disabled:opacity-50`}>
              {busy === nextStage.key ? 'Updating...' : `→ ${nextStage.label}`}
            </button>
          )}
          {!['completed', 'invoiced', 'cancelled'].includes(status) && (
            <button onClick={() => doStatus('cancelled')} disabled={!!busy}
              className="px-3 py-2 text-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition">
              Cancel
            </button>
          )}
          {status === 'cancelled' && (
            <button onClick={() => doStatus('awaiting_assignment')} disabled={!!busy}
              className="px-4 py-2 text-sm font-semibold rounded text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50">
              {busy ? 'Updating...' : 'Reinstate'}
            </button>
          )}
          <button
            className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded transition border border-gray-300 flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Clone
          </button>
        </div>
      </div>

      {/* ─── Progress stepper ─────────────────────────────────────── */}
      <div className="bg-gray-50 border-b border-gray-200 px-6 py-2.5 flex-shrink-0">
        <div className="flex items-center">
          {STAGES.map((s, i) => {
            const done = stageIdx > i;
            const current = stageIdx === i;
            const cancelled = status === 'cancelled';
            return (
              <div key={s.key} className="flex items-center flex-1">
                <div className={`flex items-center gap-1.5 ${
                  cancelled ? 'opacity-30' : current ? '' : done ? 'opacity-70' : 'opacity-30'
                }`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    done ? 'bg-green-500 text-white' : current ? `${STATUS_BG[s.key]} text-white` : 'bg-gray-300 text-white'
                  }`}>
                    {done ? '✓' : i + 1}
                  </div>
                  <span className={`text-xs font-medium ${current ? 'text-gray-900' : 'text-gray-500'}`}>
                    {s.label}
                  </span>
                </div>
                {i < STAGES.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 rounded ${done ? 'bg-green-400' : 'bg-gray-200'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Tab bar ──────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-6 flex gap-0 flex-shrink-0">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition ${
              activeTab === t.key
                ? 'border-slate-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">{t.icon}</svg>
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── Content ──────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
        {activeTab === 'overview' && <OverviewTab job={job} onTabSwitch={setActiveTab} />}
        {activeTab === 'booking' && <BookingTab job={job} onRefresh={onRefresh} setError={setError} />}
        {activeTab === 'resources' && <ResourcesTab job={job} onRefresh={onRefresh} setError={setError} />}
        {activeTab === 'costing' && <CostingTab job={job} onRefresh={onRefresh} setError={setError} />}
        {activeTab === 'briefing' && <BriefingTab job={job} setError={setError} />}
        {activeTab === 'timeline' && <JourneyTab job={job} />}
        {activeTab === 'activity' && <ActivityTab job={job} />}
      </div>
    </div>
  );
}

/* Tab icons */
const overviewIcon = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />;
const bookingIcon = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />;
const resourcesIcon = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />;
const briefingIcon = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />;
const journeyIcon = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />;
const costingIcon = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />;
const activityIcon = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />;


/* ══════════════════════════════════════════════════════════════════════
   OVERVIEW TAB — quick glance at everything, with smart prompts
   ══════════════════════════════════════════════════════════════════════ */

function OverviewTab({ job, onTabSwitch }: { job: JobDetail; onTabSwitch: (t: string) => void }) {
  const status = normaliseStatus(job.status);
  // Figure out what needs doing
  const needsBooking = !job.job_date || !job.collection_postcode || !job.delivery_postcode;
  const needsAssignment = !job.assigned_driver_id || !job.assigned_vehicle_id;
  const canDispatch = status === 'assigned';

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      {/* Smart prompts */}
      {status === 'awaiting_assignment' && (
        <PromptCard color="amber"
          title="This job needs booking in"
          desc="Add collection/delivery details, dates, load information and client contact."
          action="Go to Booking" onClick={() => onTabSwitch('booking')} />
      )}
      {(status === 'booked_in' || (status === 'awaiting_assignment' && !needsBooking)) && needsAssignment && (
        <PromptCard color="indigo"
          title="Ready for assignment"
          desc="Allocate a vehicle and driver. The system will check availability for you."
          action="Assign Resources" onClick={() => onTabSwitch('resources')} />
      )}
      {canDispatch && (
        <PromptCard color="purple"
          title="Ready to dispatch"
          desc="Review the work ticket and send the briefing to the driver."
          action="View Work Ticket" onClick={() => onTabSwitch('briefing')} />
      )}

      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Schedule */}
        <SummaryCard
          icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />}
          title="Schedule"
          onClick={() => onTabSwitch('booking')}
          items={[
            { label: 'Date', value: job.job_date || 'Not set', warn: !job.job_date },
            ...(job.job_end_date ? [{ label: 'End', value: job.job_end_date }] : []),
            { label: 'Time', value: job.start_time || 'Not set', warn: !job.start_time },
          ]}
        />
        {/* Route */}
        <SummaryCard
          icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />}
          title="Route"
          onClick={() => onTabSwitch('timeline')}
          items={[
            { label: 'Distance', value: job.total_distance_miles ? `${job.total_distance_miles.toFixed(0)} miles` : '—' },
            { label: 'Driving', value: job.total_driving_hours ? `${job.total_driving_hours.toFixed(1)} hrs` : '—' },
            { label: 'Duty', value: job.total_duty_hours ? `${job.total_duty_hours.toFixed(1)} hrs` : '—' },
            ...(job.overnight_count ? [{ label: 'Overnights', value: String(job.overnight_count) }] : []),
          ]}
        />
        {/* Resources */}
        <SummaryCard
          icon={resourcesIcon}
          title="Resources"
          onClick={() => onTabSwitch('resources')}
          items={[
            { label: 'Vehicle', value: job.vehicle_reg || 'Unassigned', warn: !job.vehicle_reg },
            { label: 'Driver', value: job.driver_name || 'Unassigned', warn: !job.driver_name },
          ]}
        />
      </div>

      {/* Collection / Delivery row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded border border-gray-200 p-5 cursor-pointer hover:shadow transition" onClick={() => onTabSwitch('booking')}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded bg-green-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-gray-800">Collection</h3>
          </div>
          {job.collection_address || job.collection_postcode ? (
            <>
              <p className="text-sm text-gray-700">{job.collection_address || '—'}</p>
              <p className="text-xs text-gray-500">{job.collection_postcode}</p>
              {job.collection_instructions && <p className="text-xs text-gray-400 mt-1 italic">{job.collection_instructions}</p>}
            </>
          ) : (
            <p className="text-sm text-gray-300 italic">Not set — click to add</p>
          )}
        </div>

        <div className="bg-white rounded border border-gray-200 p-5 cursor-pointer hover:shadow transition" onClick={() => onTabSwitch('booking')}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded bg-red-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-gray-800">Delivery</h3>
          </div>
          {job.delivery_address || job.delivery_postcode ? (
            <>
              <p className="text-sm text-gray-700">{job.delivery_address || '—'}</p>
              <p className="text-xs text-gray-500">{job.delivery_postcode}</p>
              {job.delivery_instructions && <p className="text-xs text-gray-400 mt-1 italic">{job.delivery_instructions}</p>}
            </>
          ) : (
            <p className="text-sm text-gray-300 italic">Not set — click to add</p>
          )}
        </div>
      </div>

      {/* Load & client row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Client */}
        <div className="bg-white rounded border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Client Contact</h3>
          {job.client_contact_name ? (
            <div className="text-sm space-y-1">
              <p className="font-medium text-gray-700">{job.client_contact_name}</p>
              {job.client_contact_phone && <p className="text-gray-500">{job.client_contact_phone}</p>}
              {job.client_contact_email && <p className="text-gray-500">{job.client_contact_email}</p>}
            </div>
          ) : (
            <p className="text-sm text-gray-300 italic">Not set</p>
          )}
        </div>

        {/* Load summary */}
        <div className="bg-white rounded border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Load</h3>
          {job.load_items && job.load_items.length > 0 ? (
            <div className="text-sm space-y-1">
              <p className="text-gray-600">{job.load_items.length} item{job.load_items.length !== 1 ? 's' : ''}</p>
              {job.total_weight_kg && <p className="text-gray-500">{job.total_weight_kg.toFixed(0)} kg total</p>}
              {job.load_items.some(li => li.is_hazardous) && <p className="text-red-600 font-medium">Contains hazardous goods</p>}
              {job.load_items.some(li => li.is_fragile) && <p className="text-amber-600">Contains fragile items</p>}
            </div>
          ) : job.load_description ? (
            <p className="text-sm text-gray-600">{job.load_description}</p>
          ) : (
            <p className="text-sm text-gray-300 italic">No load details</p>
          )}
          {job.special_requirements && (
            <div className="mt-2 px-2 py-1.5 bg-amber-50 rounded text-xs text-amber-700">{job.special_requirements}</div>
          )}
        </div>
      </div>

      {/* Notes */}
      {job.notes && (
        <div className="bg-white rounded border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Notes</h3>
          <pre className="text-sm text-gray-600 whitespace-pre-wrap font-sans">{job.notes}</pre>
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════
   BOOKING TAB — inline editable booking-in form
   ══════════════════════════════════════════════════════════════════════ */

function BookingTab({ job, onRefresh, setError }: {
  job: JobDetail; onRefresh: () => Promise<void>; setError: (s: string) => void;
}) {
  const [f, setF] = useState({
    client_contact_name: job.client_contact_name || '',
    client_contact_phone: job.client_contact_phone || '',
    client_contact_email: job.client_contact_email || '',
    collection_address: job.collection_address || '',
    collection_postcode: job.collection_postcode || '',
    collection_instructions: job.collection_instructions || '',
    delivery_address: job.delivery_address || '',
    delivery_postcode: job.delivery_postcode || '',
    delivery_instructions: job.delivery_instructions || '',
    load_description: job.load_description || '',
    total_weight_kg: job.total_weight_kg ? String(job.total_weight_kg) : '',
    special_requirements: job.special_requirements || '',
    po_number: job.po_number || '',
    job_date: job.job_date || '',
    job_end_date: job.job_end_date || '',
    start_time: job.start_time || '',
    notes: job.notes || '',
    job_type: job.job_type || 'haulage',
    passenger_count: job.passenger_count ? String(job.passenger_count) : '',
    vehicle_class: job.vehicle_class || '',
    wheelchair_accessible: job.wheelchair_accessible || false,
    low_floor: job.low_floor || false,
    wheelchair_positions: job.wheelchair_positions ? String(job.wheelchair_positions) : '',
  });
  const [items, setItems] = useState<LoadItem[]>(job.load_items?.length ? job.load_items : []);
  const [pickupSchedule, setPickupSchedule] = useState<Array<{ location: string; time: string }>>(job.pickup_schedule || []);
  const [dropoffSchedule, setDropoffSchedule] = useState<Array<{ location: string; time: string }>>(job.dropoff_schedule || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const isCoachMode = f.job_type === 'coach_psv';

  const set = (k: string, v: string | boolean) => setF(p => ({ ...p, [k]: v }));

  const addPickupStop = () => setPickupSchedule(p => [...p, { location: '', time: '' }]);
  const setPickupStop = (i: number, k: string, v: string) => setPickupSchedule(p => p.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const removePickupStop = (i: number) => setPickupSchedule(p => p.filter((_, j) => j !== i));

  const addDropoffStop = () => setDropoffSchedule(p => [...p, { location: '', time: '' }]);
  const setDropoffStop = (i: number, k: string, v: string) => setDropoffSchedule(p => p.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const removeDropoffStop = (i: number) => setDropoffSchedule(p => p.filter((_, j) => j !== i));

  const save = async () => {
    try {
      setSaving(true);
      await api.bookInJob(job.id, {
        ...f,
        total_weight_kg: f.total_weight_kg ? Number(f.total_weight_kg) : null,
        passenger_count: f.passenger_count ? Number(f.passenger_count) : null,
        wheelchair_positions: f.wheelchair_positions ? Number(f.wheelchair_positions) : null,
        load_items: items.filter(li => li.description.trim()),
        pickup_schedule: isCoachMode ? pickupSchedule : null,
        dropoff_schedule: isCoachMode ? dropoffSchedule : null,
      });
      await onRefresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const addItem = () => setItems(p => [...p, {
    item_order: p.length, description: '', quantity: 1, weight_kg: null,
    length_cm: null, width_cm: null, height_cm: null,
    is_fragile: false, is_hazardous: false, hazard_class: null, notes: null,
  }]);

  const setItem = (i: number, k: string, v: any) => setItems(p => p.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const removeItem = (i: number) => setItems(p => p.filter((_, j) => j !== i));

  const normStatus = normaliseStatus(job.status);
  const isEditable = !['completed', 'invoiced', 'cancelled'].includes(normStatus);

  return (
    <div className="max-w-5xl mx-auto">
      {!isEditable && (
        <div className="mb-4 p-3 bg-gray-100 text-gray-600 rounded text-sm">
          Booking details are locked — this job is {STATUS_LABELS[normStatus] || normStatus}.
        </div>
      )}

      {/* Job Type Selector */}
      <FormCard title="Job Type">
        <div className="flex gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="job_type" value="haulage" checked={!isCoachMode}
              onChange={e => set('job_type', e.target.value)} disabled={!isEditable} />
            <span className="text-sm text-gray-700 font-medium">Haulage</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="job_type" value="coach_psv" checked={isCoachMode}
              onChange={e => set('job_type', e.target.value)} disabled={!isEditable} />
            <span className="text-sm text-gray-700 font-medium">Coach & PSV</span>
          </label>
        </div>
      </FormCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        {/* Left */}
        <div className="space-y-5">
          <FormCard title="Schedule">
            <div className="grid grid-cols-3 gap-3">
              <Inp label="Job Date" type="date" value={f.job_date} onChange={v => set('job_date', v)} disabled={!isEditable} />
              <Inp label="End Date" type="date" value={f.job_end_date} onChange={v => set('job_end_date', v)} disabled={!isEditable} />
              <Inp label="Start Time" type="time" value={f.start_time} onChange={v => set('start_time', v)} disabled={!isEditable} />
            </div>
          </FormCard>

          <FormCard title="Client Contact">
            <div className="grid grid-cols-2 gap-3">
              <Inp label="Name" value={f.client_contact_name} onChange={v => set('client_contact_name', v)} disabled={!isEditable} />
              <Inp label="Phone" value={f.client_contact_phone} onChange={v => set('client_contact_phone', v)} disabled={!isEditable} />
            </div>
            <Inp label="Email" value={f.client_contact_email} onChange={v => set('client_contact_email', v)} disabled={!isEditable} className="mt-3" />
          </FormCard>

          <FormCard title="Collection">
            <Inp label="Address" value={f.collection_address} onChange={v => set('collection_address', v)} disabled={!isEditable} />
            <Inp label="Postcode" value={f.collection_postcode} onChange={v => set('collection_postcode', v)} disabled={!isEditable} className="mt-3" />
            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-500 mb-1">Instructions</label>
              <textarea value={f.collection_instructions} onChange={e => set('collection_instructions', e.target.value)}
                disabled={!isEditable} rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-50 disabled:text-gray-500" />
            </div>
          </FormCard>

          <FormCard title="Delivery">
            <Inp label="Address" value={f.delivery_address} onChange={v => set('delivery_address', v)} disabled={!isEditable} />
            <Inp label="Postcode" value={f.delivery_postcode} onChange={v => set('delivery_postcode', v)} disabled={!isEditable} className="mt-3" />
            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-500 mb-1">Instructions</label>
              <textarea value={f.delivery_instructions} onChange={e => set('delivery_instructions', e.target.value)}
                disabled={!isEditable} rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-50 disabled:text-gray-500" />
            </div>
          </FormCard>
        </div>

        {/* Right */}
        <div className="space-y-5">
          <FormCard title="Reference">
            <div className="grid grid-cols-2 gap-3">
              <Inp label="PO Number" value={f.po_number} onChange={v => set('po_number', v)} disabled={!isEditable} />
              <Inp label="Total Weight (kg)" type="number" value={f.total_weight_kg} onChange={v => set('total_weight_kg', v)} disabled={!isEditable} />
            </div>
            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-500 mb-1">Special Requirements</label>
              <textarea value={f.special_requirements} onChange={e => set('special_requirements', e.target.value)}
                disabled={!isEditable} rows={2} placeholder="Tail lift, HIAB, temp controlled..."
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-50 disabled:text-gray-500" />
            </div>
          </FormCard>

          {!isCoachMode && (
            <FormCard title="Load Description">
              <textarea value={f.load_description} onChange={e => set('load_description', e.target.value)}
                disabled={!isEditable} rows={2} placeholder="General description..."
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-50 disabled:text-gray-500" />
            </FormCard>
          )}

          {isCoachMode && (
            <FormCard title="Passenger & Vehicle">
              <div className="grid grid-cols-2 gap-3">
                <Inp label="Passenger Count" type="number" value={f.passenger_count} onChange={v => set('passenger_count', v)} disabled={!isEditable} />
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Vehicle Class</label>
                  <select value={f.vehicle_class} onChange={e => set('vehicle_class', e.target.value)}
                    disabled={!isEditable}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-50 disabled:text-gray-500">
                    <option value="">Select...</option>
                    <option value="Minibus">Minibus</option>
                    <option value="Minicoach">Minicoach</option>
                    <option value="Midi">Midi</option>
                    <option value="Standard">Standard</option>
                    <option value="Executive">Executive</option>
                    <option value="Double Decker">Double Decker</option>
                  </select>
                </div>
              </div>
              <div className="mt-3 space-y-2">
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input type="checkbox" checked={f.wheelchair_accessible} onChange={e => set('wheelchair_accessible', e.target.checked)} disabled={!isEditable} />
                  Wheelchair accessible
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input type="checkbox" checked={f.low_floor} onChange={e => set('low_floor', e.target.checked)} disabled={!isEditable} />
                  Low floor
                </label>
                {f.wheelchair_accessible && (
                  <Inp label="Wheelchair Positions" type="number" value={f.wheelchair_positions} onChange={v => set('wheelchair_positions', v)} disabled={!isEditable} />
                )}
              </div>
            </FormCard>
          )}

          {!isCoachMode && <FormCard title="Load Items">
            {items.map((li, i) => (
              <div key={i} className="bg-gray-50 rounded p-3 mb-3 border border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase">Item {i + 1}</span>
                  {isEditable && (
                    <button onClick={() => removeItem(i)} className="text-[10px] text-red-400 hover:text-red-600 font-medium">Remove</button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <input value={li.description} onChange={e => setItem(i, 'description', e.target.value)}
                    disabled={!isEditable} placeholder="Description"
                    className="col-span-2 px-2 py-1.5 border border-gray-200 rounded text-sm bg-white disabled:bg-gray-50" />
                  <input type="number" value={li.quantity} onChange={e => setItem(i, 'quantity', parseInt(e.target.value) || 1)}
                    disabled={!isEditable} placeholder="Qty"
                    className="px-2 py-1.5 border border-gray-200 rounded text-sm bg-white disabled:bg-gray-50" />
                  <input type="number" value={li.weight_kg ?? ''} onChange={e => setItem(i, 'weight_kg', e.target.value ? parseFloat(e.target.value) : null)}
                    disabled={!isEditable} placeholder="Weight kg"
                    className="px-2 py-1.5 border border-gray-200 rounded text-sm bg-white disabled:bg-gray-50" />
                  <input type="number" value={li.length_cm ?? ''} onChange={e => setItem(i, 'length_cm', e.target.value ? parseFloat(e.target.value) : null)}
                    disabled={!isEditable} placeholder="L cm"
                    className="px-2 py-1.5 border border-gray-200 rounded text-sm bg-white disabled:bg-gray-50" />
                  <input type="number" value={li.width_cm ?? ''} onChange={e => setItem(i, 'width_cm', e.target.value ? parseFloat(e.target.value) : null)}
                    disabled={!isEditable} placeholder="W cm"
                    className="px-2 py-1.5 border border-gray-200 rounded text-sm bg-white disabled:bg-gray-50" />
                </div>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-1.5 text-xs text-gray-600">
                    <input type="checkbox" checked={li.is_fragile} onChange={e => setItem(i, 'is_fragile', e.target.checked)} disabled={!isEditable} />
                    Fragile
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-600">
                    <input type="checkbox" checked={li.is_hazardous} onChange={e => setItem(i, 'is_hazardous', e.target.checked)} disabled={!isEditable} />
                    Hazardous
                  </label>
                  {li.is_hazardous && (
                    <input value={li.hazard_class || ''} onChange={e => setItem(i, 'hazard_class', e.target.value)}
                      disabled={!isEditable} placeholder="Class"
                      className="px-2 py-0.5 border border-gray-200 rounded text-xs w-24 bg-white" />
                  )}
                </div>
              </div>
            ))}
            {isEditable && (
              <button onClick={addItem}
                className="w-full py-2.5 border-2 border-dashed border-gray-300 rounded text-sm text-gray-400 hover:border-blue-400 hover:text-blue-600 transition">
                + Add Load Item
              </button>
            )}
          </FormCard>}

          {isCoachMode && (
            <>
              <FormCard title="Pick-up Schedule">
                {pickupSchedule.map((stop, i) => (
                  <div key={i} className="bg-gray-50 rounded p-3 mb-3 border border-gray-100">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase">Stop {i + 1}</span>
                      {isEditable && (
                        <button onClick={() => removePickupStop(i)} className="text-[10px] text-red-400 hover:text-red-600 font-medium">Remove</button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={stop.location} onChange={e => setPickupStop(i, 'location', e.target.value)}
                        disabled={!isEditable} placeholder="Location"
                        className="col-span-1 px-2 py-1.5 border border-gray-200 rounded text-sm bg-white disabled:bg-gray-50" />
                      <input type="time" value={stop.time} onChange={e => setPickupStop(i, 'time', e.target.value)}
                        disabled={!isEditable} placeholder="Time"
                        className="col-span-1 px-2 py-1.5 border border-gray-200 rounded text-sm bg-white disabled:bg-gray-50" />
                    </div>
                  </div>
                ))}
                {isEditable && (
                  <button onClick={addPickupStop}
                    className="w-full py-2.5 border-2 border-dashed border-gray-300 rounded text-sm text-gray-400 hover:border-blue-400 hover:text-blue-600 transition">
                    + Add Pick-up Stop
                  </button>
                )}
              </FormCard>

              <FormCard title="Drop-off Schedule">
                {dropoffSchedule.map((stop, i) => (
                  <div key={i} className="bg-gray-50 rounded p-3 mb-3 border border-gray-100">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase">Stop {i + 1}</span>
                      {isEditable && (
                        <button onClick={() => removeDropoffStop(i)} className="text-[10px] text-red-400 hover:text-red-600 font-medium">Remove</button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={stop.location} onChange={e => setDropoffStop(i, 'location', e.target.value)}
                        disabled={!isEditable} placeholder="Location"
                        className="col-span-1 px-2 py-1.5 border border-gray-200 rounded text-sm bg-white disabled:bg-gray-50" />
                      <input type="time" value={stop.time} onChange={e => setDropoffStop(i, 'time', e.target.value)}
                        disabled={!isEditable} placeholder="Time"
                        className="col-span-1 px-2 py-1.5 border border-gray-200 rounded text-sm bg-white disabled:bg-gray-50" />
                    </div>
                  </div>
                ))}
                {isEditable && (
                  <button onClick={addDropoffStop}
                    className="w-full py-2.5 border-2 border-dashed border-gray-300 rounded text-sm text-gray-400 hover:border-blue-400 hover:text-blue-600 transition">
                    + Add Drop-off Stop
                  </button>
                )}
              </FormCard>
            </>
          )}

          <FormCard title="Notes">
            <textarea value={f.notes} onChange={e => set('notes', e.target.value)}
              disabled={!isEditable} rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-50 disabled:text-gray-500" />
          </FormCard>
        </div>
      </div>

      {/* Save bar */}
      {isEditable && (
        <div className="sticky bottom-0 mt-5 -mx-6 px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-end gap-3">
          {saved && <span className="text-sm text-green-600 font-medium">Saved successfully</span>}
          <button onClick={save} disabled={saving}
            className="px-6 py-2.5 bg-slate-700 text-white text-sm font-semibold rounded hover:bg-slate-800 disabled:opacity-50 transition">
            {saving ? 'Saving...' : 'Save Booking Details'}
          </button>
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════
   RESOURCES TAB — vehicle & driver assignment with availability
   ══════════════════════════════════════════════════════════════════════ */

function ResourcesTab({ job, onRefresh, setError }: {
  job: JobDetail; onRefresh: () => Promise<void>; setError: (s: string) => void;
}) {
  const [vehicles, setVehicles] = useState<AvailResource[]>([]);
  const [drivers, setDrivers] = useState<AvailResource[]>([]);
  const [selVehicle, setSelVehicle] = useState<number | null>(job.assigned_vehicle_id);
  const [selDriver, setSelDriver] = useState<number | null>(job.assigned_driver_id);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [isSubcontracted, setIsSubcontracted] = useState(job.is_sub_contracted || false);
  const [subcontrForm, setSubcontrForm] = useState({
    sub_contractor_name: job.sub_contractor_name || '',
    sub_contractor_contact: job.sub_contractor_contact || '',
    sub_contractor_phone: job.sub_contractor_phone || '',
    sub_contractor_agreed_price: job.sub_contractor_agreed_price ? String(job.sub_contractor_agreed_price) : '',
    sub_contractor_po: job.sub_contractor_po || '',
  });
  const [subcontrSaving, setSubcontrSaving] = useState(false);

  useEffect(() => {
    if (!job.job_date) return;
    (async () => {
      try {
        setLoading(true);
        const [v, d] = await Promise.all([
          api.getAvailableVehicles(job.job_date!, job.job_end_date || undefined),
          api.getAvailableDrivers(job.job_date!, job.job_end_date || undefined),
        ]);
        setVehicles(v);
        setDrivers(d);
      } catch (e: any) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [job.job_date, job.job_end_date]);

  const assign = async () => {
    if (!selVehicle || !selDriver) return;
    try {
      setAssigning(true);
      await api.assignJob(job.id, { assigned_vehicle_id: selVehicle, assigned_driver_id: selDriver });
      await onRefresh();
    } catch (e: any) { setError(e.message); }
    finally { setAssigning(false); }
  };

  const saveSubcontractor = async () => {
    try {
      setSubcontrSaving(true);
      await api.updateJob(job.id, {
        is_sub_contracted: isSubcontracted,
        sub_contractor_name: subcontrForm.sub_contractor_name || null,
        sub_contractor_contact: subcontrForm.sub_contractor_contact || null,
        sub_contractor_phone: subcontrForm.sub_contractor_phone || null,
        sub_contractor_agreed_price: subcontrForm.sub_contractor_agreed_price ? Number(subcontrForm.sub_contractor_agreed_price) : null,
        sub_contractor_po: subcontrForm.sub_contractor_po || null,
      });
      await onRefresh();
    } catch (e: any) { setError(e.message); }
    finally { setSubcontrSaving(false); }
  };

  const normStatus = normaliseStatus(job.status);
  const canAssign = !['completed', 'invoiced', 'cancelled'].includes(normStatus);

  if (!job.job_date) {
    return (
      <div className="max-w-xl mx-auto text-center py-16">
        <div className="w-16 h-16 bg-amber-100 rounded flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-1">Set a date first</h3>
        <p className="text-sm text-gray-500">The job needs a date before we can check vehicle and driver availability.</p>
        <p className="text-xs text-gray-400 mt-2">Go to the Booking tab to set dates.</p>
      </div>
    );
  }

  const availVehicles = vehicles.filter(v => v.is_available);
  const unavailVehicles = vehicles.filter(v => !v.is_available);
  const availDrivers = drivers.filter(d => d.is_available);
  const unavailDrivers = drivers.filter(d => !d.is_available);

  return (
    <div className="max-w-5xl mx-auto">
      {/* Sub-contractor section */}
      <FormCard title="Assignment Mode">
        <div className="space-y-3">
          <div className="flex gap-4 items-center">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="assignment_mode" checked={!isSubcontracted} onChange={() => setIsSubcontracted(false)} />
              <span className="text-sm text-gray-700 font-medium">Operate In-house</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="assignment_mode" checked={isSubcontracted} onChange={() => setIsSubcontracted(true)} />
              <span className="text-sm text-gray-700 font-medium">Sub-contract</span>
            </label>
          </div>

          {isSubcontracted && (
            <div className="bg-blue-50 rounded p-4 border border-blue-200 space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Company Name</label>
                  <input type="text" value={subcontrForm.sub_contractor_name} onChange={e => setSubcontrForm(p => ({ ...p, sub_contractor_name: e.target.value }))}
                    placeholder="Sub-contractor name"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Contact Person</label>
                  <input type="text" value={subcontrForm.sub_contractor_contact} onChange={e => setSubcontrForm(p => ({ ...p, sub_contractor_contact: e.target.value }))}
                    placeholder="Contact name"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
                  <input type="text" value={subcontrForm.sub_contractor_phone} onChange={e => setSubcontrForm(p => ({ ...p, sub_contractor_phone: e.target.value }))}
                    placeholder="Phone number"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Agreed Price</label>
                  <input type="number" value={subcontrForm.sub_contractor_agreed_price} onChange={e => setSubcontrForm(p => ({ ...p, sub_contractor_agreed_price: e.target.value }))}
                    placeholder="£0"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">PO Reference</label>
                <input type="text" value={subcontrForm.sub_contractor_po} onChange={e => setSubcontrForm(p => ({ ...p, sub_contractor_po: e.target.value }))}
                  placeholder="PO-2024-001"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
              </div>
              <button onClick={saveSubcontractor} disabled={subcontrSaving}
                className="w-full px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 transition">
                {subcontrSaving ? 'Saving...' : 'Save Sub-contractor'}
              </button>
            </div>
          )}
        </div>
      </FormCard>

      {/* Current assignment */}
      {!isSubcontracted && (job.assigned_vehicle_id || job.assigned_driver_id) && (
        <div className="bg-indigo-50 border border-indigo-200 rounded p-4 mb-5 flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-indigo-200 flex items-center justify-center">
              <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="text-sm font-medium text-indigo-800">Currently assigned:</span>
          </div>
          <span className="text-sm text-indigo-700">{job.vehicle_reg || '—'}</span>
          <span className="text-sm text-indigo-700">{job.driver_name || '—'}</span>
          {job.assigned_at && <span className="text-xs text-indigo-400">{new Date(job.assigned_at).toLocaleDateString()}</span>}
        </div>
      )}

      {!isSubcontracted && (
        <>
          <div className="flex items-center justify-between mb-4 mt-5">
            <p className="text-sm text-gray-500">
              Availability for <span className="font-medium text-gray-700">{job.job_date}</span>
              {job.job_end_date && <span> to <span className="font-medium text-gray-700">{job.job_end_date}</span></span>}
            </p>
            <label className="flex items-center gap-2 text-xs text-gray-500">
              <input type="checkbox" checked={showUnavailable} onChange={e => setShowUnavailable(e.target.checked)} />
              Show unavailable
            </label>
          </div>

          {loading ? (
            <div className="text-center py-16 text-gray-400">Checking availability...</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Vehicles */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              Vehicles
              <span className="text-xs text-gray-400 font-normal">({availVehicles.length} available)</span>
            </h3>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
              {availVehicles.map(v => (
                <ResourceCard key={v.id} resource={v} type="vehicle"
                  selected={selVehicle === v.id} onSelect={() => canAssign && setSelVehicle(v.id)} />
              ))}
              {showUnavailable && unavailVehicles.map(v => (
                <ResourceCard key={v.id} resource={v} type="vehicle"
                  selected={selVehicle === v.id} onSelect={() => canAssign && setSelVehicle(v.id)} />
              ))}
              {vehicles.length === 0 && (
                <p className="text-sm text-gray-400 py-4 text-center">No vehicles found</p>
              )}
            </div>
          </div>

          {/* Drivers */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Drivers
              <span className="text-xs text-gray-400 font-normal">({availDrivers.length} available)</span>
            </h3>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
              {availDrivers.map(d => (
                <ResourceCard key={d.id} resource={d} type="driver"
                  selected={selDriver === d.id} onSelect={() => canAssign && setSelDriver(d.id)} />
              ))}
              {showUnavailable && unavailDrivers.map(d => (
                <ResourceCard key={d.id} resource={d} type="driver"
                  selected={selDriver === d.id} onSelect={() => canAssign && setSelDriver(d.id)} />
              ))}
              {drivers.length === 0 && (
                <p className="text-sm text-gray-400 py-4 text-center">No drivers found</p>
              )}
            </div>
          </div>
        </div>
            )}
        </>
      )}

      {/* Assign bar */}
      {!isSubcontracted && canAssign && (
        <div className="sticky bottom-0 mt-5 -mx-6 px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {selVehicle && selDriver ? (
              <span>
                Assigning <strong>{vehicles.find(v => v.id === selVehicle)?.registration}</strong> + <strong>{drivers.find(d => d.id === selDriver)?.name}</strong>
              </span>
            ) : (
              <span className="text-gray-400">Select a vehicle and driver above</span>
            )}
          </div>
          <button onClick={assign} disabled={!selVehicle || !selDriver || assigning}
            className="px-6 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded hover:bg-indigo-700 disabled:opacity-40 transition">
            {assigning ? 'Assigning...' : 'Confirm Assignment'}
          </button>
        </div>
      )}
    </div>
  );
}

function ResourceCard({ resource, type, selected, onSelect }: {
  resource: AvailResource; type: 'vehicle' | 'driver';
  selected: boolean; onSelect: () => void;
}) {
  return (
    <div onClick={onSelect}
      className={`p-3.5 rounded border-2 cursor-pointer transition-all ${
        selected ? 'border-slate-500 bg-blue-50 shadow-sm' :
        resource.is_available ? 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm' :
        'border-red-200 bg-red-50/50 opacity-70'
      }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {selected && (
            <div className="w-5 h-5 rounded-full bg-slate-600 flex items-center justify-center flex-shrink-0">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          <div>
            <p className="text-sm font-semibold text-gray-800">
              {type === 'vehicle' ? resource.registration : resource.name}
            </p>
            {type === 'vehicle' && resource.make && (
              <p className="text-xs text-gray-400">{resource.make} {resource.model}</p>
            )}
            {type === 'driver' && resource.phone && (
              <p className="text-xs text-gray-400">{resource.phone}</p>
            )}
          </div>
        </div>
        {resource.is_available ? (
          <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-green-100 text-green-700">Available</span>
        ) : (
          <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-red-100 text-red-700">Conflict</span>
        )}
      </div>
      {resource.conflicts.length > 0 && (
        <div className="mt-2 pl-8 space-y-0.5">
          {resource.conflicts.map((c, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs text-red-600">
              <span className="text-red-400 mt-0.5">•</span>
              <span>{c.detail}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════
   COSTING TAB — revenue, costs, and margin calculation
   ══════════════════════════════════════════════════════════════════════ */

function CostingTab({ job, onRefresh, setError }: {
  job: JobDetail; onRefresh: () => Promise<void>; setError: (s: string) => void;
}) {
  const [costs, setCosts] = useState({
    quoted_price: job.quoted_price ? String(job.quoted_price) : '',
    agreed_price: job.agreed_price ? String(job.agreed_price) : '',
    additional_charges: job.additional_charges ? String(job.additional_charges) : '',
    fuel_cost: job.fuel_cost ? String(job.fuel_cost) : '',
    fuel_rate_per_mile: job.fuel_rate_per_mile ? String(job.fuel_rate_per_mile) : '0.50',
    driver_wages_hours: job.driver_wages_hours ? String(job.driver_wages_hours) : '',
    driver_rate_per_hour: job.driver_rate_per_hour ? String(job.driver_rate_per_hour) : '15',
    vehicle_running_cost: job.vehicle_running_cost ? String(job.vehicle_running_cost) : '',
    vehicle_rate_per_mile: job.vehicle_rate_per_mile ? String(job.vehicle_rate_per_mile) : '0.30',
    tolls_charges: job.tolls_charges ? String(job.tolls_charges) : '',
    parking_congestion: job.parking_congestion ? String(job.parking_congestion) : '',
    overnight_allowance_rate: job.overnight_allowance_rate ? String(job.overnight_allowance_rate) : '25',
    sub_contractor_cost: job.sub_contractor_cost ? String(job.sub_contractor_cost) : '',
    other_costs: job.other_costs || '',
    other_costs_amount: job.other_costs_amount ? String(job.other_costs_amount) : '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const setCost = (k: string, v: string) => setCosts(p => ({ ...p, [k]: v }));

  // Auto-calculate fuel cost if distance is available
  const fuelCost = job.total_distance_miles && costs.fuel_rate_per_mile
    ? job.total_distance_miles * parseFloat(costs.fuel_rate_per_mile)
    : 0;

  // Auto-calculate driver wages
  const driverWages = costs.driver_wages_hours && costs.driver_rate_per_hour
    ? parseFloat(costs.driver_wages_hours) * parseFloat(costs.driver_rate_per_hour)
    : 0;

  // Auto-calculate vehicle running cost
  const vehicleRunning = job.total_distance_miles && costs.vehicle_rate_per_mile
    ? job.total_distance_miles * parseFloat(costs.vehicle_rate_per_mile)
    : 0;

  // Calculate overnight allowance
  const overnightAllowance = (job.overnight_count || 0) * parseFloat(costs.overnight_allowance_rate || '0');

  // Total costs
  const totalCosts = (
    fuelCost +
    driverWages +
    vehicleRunning +
    (parseFloat(costs.tolls_charges) || 0) +
    (parseFloat(costs.parking_congestion) || 0) +
    overnightAllowance +
    (parseFloat(costs.sub_contractor_cost) || 0) +
    (parseFloat(costs.other_costs_amount) || 0)
  );

  // Revenue
  const agreedPrice = parseFloat(costs.agreed_price) || 0;
  const additionalCharges = parseFloat(costs.additional_charges) || 0;
  const totalRevenue = agreedPrice + additionalCharges;

  // Profit/Loss
  const profit = totalRevenue - totalCosts;
  const profitPercent = totalRevenue > 0 ? (profit / totalRevenue) * 100 : 0;

  // Margin indicator color
  let marginColor = 'bg-red-100 text-red-700';
  if (profitPercent >= 15) marginColor = 'bg-green-100 text-green-700';
  else if (profitPercent >= 5) marginColor = 'bg-amber-100 text-amber-700';

  const save = async () => {
    try {
      setSaving(true);
      await api.updateJob(job.id, {
        quoted_price: costs.quoted_price ? Number(costs.quoted_price) : null,
        agreed_price: costs.agreed_price ? Number(costs.agreed_price) : null,
        additional_charges: costs.additional_charges ? Number(costs.additional_charges) : null,
        fuel_cost: fuelCost || null,
        fuel_rate_per_mile: costs.fuel_rate_per_mile ? Number(costs.fuel_rate_per_mile) : null,
        driver_wages_hours: costs.driver_wages_hours ? Number(costs.driver_wages_hours) : null,
        driver_rate_per_hour: costs.driver_rate_per_hour ? Number(costs.driver_rate_per_hour) : null,
        vehicle_running_cost: vehicleRunning || null,
        vehicle_rate_per_mile: costs.vehicle_rate_per_mile ? Number(costs.vehicle_rate_per_mile) : null,
        tolls_charges: costs.tolls_charges ? Number(costs.tolls_charges) : null,
        parking_congestion: costs.parking_congestion ? Number(costs.parking_congestion) : null,
        overnight_allowance_rate: costs.overnight_allowance_rate ? Number(costs.overnight_allowance_rate) : null,
        sub_contractor_cost: costs.sub_contractor_cost ? Number(costs.sub_contractor_cost) : null,
        other_costs: costs.other_costs || null,
        other_costs_amount: costs.other_costs_amount ? Number(costs.other_costs_amount) : null,
      });
      await onRefresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const normStatus = normaliseStatus(job.status);
  const isEditable = !['completed', 'invoiced', 'cancelled'].includes(normStatus);

  return (
    <div className="max-w-5xl mx-auto">
      {!isEditable && (
        <div className="mb-4 p-3 bg-gray-100 text-gray-600 rounded text-sm">
          Costing is locked — this job is {STATUS_LABELS[normStatus] || normStatus}.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Revenue */}
        <FormCard title="Revenue">
          <div className="space-y-3">
            <Inp label="Quoted Price" type="number" value={costs.quoted_price} onChange={v => setCost('quoted_price', v)} disabled={!isEditable} />
            <Inp label="Agreed Price" type="number" value={costs.agreed_price} onChange={v => setCost('agreed_price', v)} disabled={!isEditable} />
            <Inp label="Additional Charges" type="number" value={costs.additional_charges} onChange={v => setCost('additional_charges', v)} disabled={!isEditable} />
            <div className="bg-blue-50 rounded p-3 border border-blue-200 mt-3">
              <p className="text-xs text-blue-600 font-medium">Total Revenue</p>
              <p className="text-xl font-bold text-blue-700">£{totalRevenue.toFixed(2)}</p>
            </div>
          </div>
        </FormCard>

        {/* Costs Breakdown */}
        <FormCard title="Cost Breakdown">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center pb-2 border-b border-gray-200">
              <span className="text-gray-600">Fuel cost (auto)</span>
              <span className="font-medium text-gray-800">£{fuelCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center pb-2 border-b border-gray-200">
              <span className="text-gray-600">Driver wages (auto)</span>
              <span className="font-medium text-gray-800">£{driverWages.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center pb-2 border-b border-gray-200">
              <span className="text-gray-600">Vehicle running (auto)</span>
              <span className="font-medium text-gray-800">£{vehicleRunning.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center pb-2 border-b border-gray-200">
              <span className="text-gray-600">Overnight allowance</span>
              <span className="font-medium text-gray-800">£{overnightAllowance.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center pb-2 border-b border-gray-200">
              <span className="text-gray-600">Tolls & charges</span>
              <span className="font-medium text-gray-800">£{(parseFloat(costs.tolls_charges) || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center pb-2 border-b border-gray-200">
              <span className="text-gray-600">Parking & congestion</span>
              <span className="font-medium text-gray-800">£{(parseFloat(costs.parking_congestion) || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center pb-2 border-b border-gray-200">
              <span className="text-gray-600">Sub-contractor</span>
              <span className="font-medium text-gray-800">£{(parseFloat(costs.sub_contractor_cost) || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center pb-2 border-b border-gray-200">
              <span className="text-gray-600">Other ({costs.other_costs})</span>
              <span className="font-medium text-gray-800">£{(parseFloat(costs.other_costs_amount) || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center pt-2 font-bold text-base">
              <span>TOTAL COSTS</span>
              <span>£{totalCosts.toFixed(2)}</span>
            </div>
          </div>
        </FormCard>
      </div>

      {/* Editable cost inputs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
        <FormCard title="Fuel (per mile)">
          <Inp label="Rate" type="number" value={costs.fuel_rate_per_mile} onChange={v => setCost('fuel_rate_per_mile', v)} disabled={!isEditable} placeholder="0.50" />
          {job.total_distance_miles && <p className="text-xs text-gray-500 mt-2">{job.total_distance_miles.toFixed(0)} miles × {costs.fuel_rate_per_mile} = £{fuelCost.toFixed(2)}</p>}
        </FormCard>

        <FormCard title="Driver Wage (per hour)">
          <Inp label="Hours" type="number" value={costs.driver_wages_hours} onChange={v => setCost('driver_wages_hours', v)} disabled={!isEditable} />
          <Inp label="Rate/hr" type="number" value={costs.driver_rate_per_hour} onChange={v => setCost('driver_rate_per_hour', v)} disabled={!isEditable} placeholder="15" className="mt-3" />
          <p className="text-xs text-gray-500 mt-2">{costs.driver_wages_hours || '0'} hrs × £{costs.driver_rate_per_hour} = £{driverWages.toFixed(2)}</p>
        </FormCard>

        <FormCard title="Vehicle Running (per mile)">
          <Inp label="Rate" type="number" value={costs.vehicle_rate_per_mile} onChange={v => setCost('vehicle_rate_per_mile', v)} disabled={!isEditable} placeholder="0.30" />
          {job.total_distance_miles && <p className="text-xs text-gray-500 mt-2">{job.total_distance_miles.toFixed(0)} miles × {costs.vehicle_rate_per_mile} = £{vehicleRunning.toFixed(2)}</p>}
        </FormCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
        <FormCard title="Tolls & Charges">
          <Inp label="Amount" type="number" value={costs.tolls_charges} onChange={v => setCost('tolls_charges', v)} disabled={!isEditable} placeholder="0" />
        </FormCard>

        <FormCard title="Parking & Congestion">
          <Inp label="Amount" type="number" value={costs.parking_congestion} onChange={v => setCost('parking_congestion', v)} disabled={!isEditable} placeholder="0" />
        </FormCard>

        <FormCard title="Overnight Allowance (per night)">
          <Inp label="Rate" type="number" value={costs.overnight_allowance_rate} onChange={v => setCost('overnight_allowance_rate', v)} disabled={!isEditable} placeholder="25" />
          {job.overnight_count && <p className="text-xs text-gray-500 mt-2">{job.overnight_count} nights × £{costs.overnight_allowance_rate} = £{overnightAllowance.toFixed(2)}</p>}
        </FormCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        <FormCard title="Sub-contractor Cost">
          <Inp label="Amount" type="number" value={costs.sub_contractor_cost} onChange={v => setCost('sub_contractor_cost', v)} disabled={!isEditable} placeholder="0" />
        </FormCard>

        <FormCard title="Other Costs">
          <Inp label="Description" value={costs.other_costs} onChange={v => setCost('other_costs', v)} disabled={!isEditable} placeholder="e.g., Permits, escorts..." />
          <Inp label="Amount" type="number" value={costs.other_costs_amount} onChange={v => setCost('other_costs_amount', v)} disabled={!isEditable} placeholder="0" className="mt-3" />
        </FormCard>
      </div>

      {/* Profit/Loss summary */}
      <div className="mt-5 bg-white rounded border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-400 uppercase font-medium">Revenue</p>
            <p className="text-2xl font-bold text-gray-900">£{totalRevenue.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 uppercase font-medium">Total Costs</p>
            <p className="text-2xl font-bold text-gray-900">£{totalCosts.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 uppercase font-medium">Profit/Loss</p>
            <p className={`text-2xl font-bold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {profit >= 0 ? '+' : ''}£{profit.toFixed(2)}
            </p>
          </div>
          <div className={`rounded p-4 ${marginColor}`}>
            <p className="text-xs font-medium uppercase">Margin</p>
            <p className="text-2xl font-bold">{profitPercent.toFixed(1)}%</p>
          </div>
        </div>
      </div>

      {/* Save bar */}
      {isEditable && (
        <div className="sticky bottom-0 mt-5 -mx-6 px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-end gap-3">
          {saved && <span className="text-sm text-green-600 font-medium">Saved successfully</span>}
          <button onClick={save} disabled={saving}
            className="px-6 py-2.5 bg-slate-700 text-white text-sm font-semibold rounded hover:bg-slate-800 disabled:opacity-50 transition">
            {saving ? 'Saving...' : 'Save Costing'}
          </button>
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════
   BRIEFING TAB — printable work ticket
   ══════════════════════════════════════════════════════════════════════ */

function BriefingTab({ job, setError }: { job: JobDetail; setError: (s: string) => void }) {
  const [briefing, setBriefing] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const b = await api.getJobBriefing(job.id);
        setBriefing(b);
      } catch (e: any) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [job.id]);

  if (loading) return <div className="text-center py-16 text-gray-400">Loading work ticket...</div>;

  if (!briefing) {
    return (
      <div className="max-w-xl mx-auto text-center py-16">
        <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {briefingIcon}
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-1">No briefing available</h3>
        <p className="text-sm text-gray-500">Assign a vehicle and driver to generate the work ticket.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex justify-end mb-4">
        <button onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white text-sm font-medium rounded hover:bg-gray-900 transition">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Print Work Ticket
        </button>
      </div>

      <div className="bg-white border border-gray-300 rounded p-8 shadow print:shadow-none print:border-0" id="work-ticket">
        {/* Header */}
        <div className="border-b-2 border-gray-900 pb-4 mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight">WORK TICKET</h1>
            <p className="text-sm text-gray-500 font-medium">Craner Kirkman Transport Ltd</p>
          </div>
          <div className="text-right">
            <p className="text-xl font-black text-blue-600">{briefing.job_number}</p>
            <p className="text-sm text-gray-600 font-medium">{briefing.job_date}{briefing.start_time ? ` · ${briefing.start_time}` : ''}</p>
            {briefing.job_end_date && <p className="text-xs text-gray-400">End: {briefing.job_end_date}</p>}
          </div>
        </div>

        {/* Key info */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <BriefField label="Customer" value={briefing.customer_name} sub={briefing.po_number ? `PO: ${briefing.po_number}` : undefined} />
          <BriefField label="Driver" value={briefing.driver?.name} sub={briefing.driver?.phone} />
          <BriefField label="Vehicle" value={briefing.vehicle?.registration} sub={[briefing.vehicle?.make, briefing.vehicle?.model].filter(Boolean).join(' ')} />
        </div>

        {/* Client contact */}
        {briefing.client_contact?.name && (
          <div className="bg-gray-50 rounded p-4 mb-5">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Client Contact</p>
            <p className="text-sm font-medium">{briefing.client_contact.name}</p>
            <p className="text-xs text-gray-500">{[briefing.client_contact.phone, briefing.client_contact.email].filter(Boolean).join(' · ')}</p>
          </div>
        )}

        {/* Collection / Delivery */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div className="border-2 border-green-200 rounded p-4">
            <p className="text-[10px] font-black text-green-700 uppercase tracking-wider mb-2">COLLECT</p>
            <p className="text-sm font-medium">{briefing.collection?.address || '—'}</p>
            {briefing.collection?.postcode && <p className="text-sm text-gray-600 font-semibold">{briefing.collection.postcode}</p>}
            {briefing.collection?.instructions && <p className="text-xs text-gray-500 mt-2 italic border-t border-green-100 pt-2">{briefing.collection.instructions}</p>}
          </div>
          <div className="border-2 border-red-200 rounded p-4">
            <p className="text-[10px] font-black text-red-700 uppercase tracking-wider mb-2">DELIVER</p>
            <p className="text-sm font-medium">{briefing.delivery?.address || '—'}</p>
            {briefing.delivery?.postcode && <p className="text-sm text-gray-600 font-semibold">{briefing.delivery.postcode}</p>}
            {briefing.delivery?.instructions && <p className="text-xs text-gray-500 mt-2 italic border-t border-red-100 pt-2">{briefing.delivery.instructions}</p>}
          </div>
        </div>

        {/* Route summary */}
        <div className="bg-blue-50 rounded p-4 mb-5 grid grid-cols-4 gap-4 text-center">
          <div><p className="text-sm font-semibold text-blue-700">{briefing.route_summary?.total_distance_miles?.toFixed(0) || '—'}</p><p className="text-[10px] text-blue-500 uppercase">miles</p></div>
          <div><p className="text-sm font-semibold text-blue-700">{briefing.route_summary?.total_driving_hours?.toFixed(1) || '—'}</p><p className="text-[10px] text-blue-500 uppercase">drive hrs</p></div>
          <div><p className="text-sm font-semibold text-blue-700">{briefing.route_summary?.total_duty_hours?.toFixed(1) || '—'}</p><p className="text-[10px] text-blue-500 uppercase">duty hrs</p></div>
          <div><p className="text-sm font-semibold text-blue-700">{briefing.route_summary?.overnight_count || 0}</p><p className="text-[10px] text-blue-500 uppercase">overnights</p></div>
        </div>

        {/* Special requirements */}
        {briefing.special_requirements && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded p-4 mb-5">
            <p className="text-[10px] font-black text-amber-700 uppercase tracking-wider mb-1">SPECIAL REQUIREMENTS</p>
            <p className="text-sm text-amber-900 font-medium">{briefing.special_requirements}</p>
          </div>
        )}

        {/* Load manifest */}
        {briefing.load_items?.length > 0 && (
          <div className="mb-5">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Load Manifest</p>
            <table className="min-w-full text-sm border border-gray-200 rounded overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Item</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Qty</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Weight</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Flags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {briefing.load_items.map((li: any, i: number) => (
                  <tr key={i}>
                    <td className="px-3 py-2 font-medium">{li.description}</td>
                    <td className="px-3 py-2 text-center">{li.quantity}</td>
                    <td className="px-3 py-2 text-right">{li.weight_kg ? `${li.weight_kg} kg` : '—'}</td>
                    <td className="px-3 py-2 text-center text-xs">
                      {li.is_fragile && <span className="text-amber-600 font-semibold mr-1">FRAGILE</span>}
                      {li.is_hazardous && <span className="text-red-600 font-semibold">HAZ {li.hazard_class || ''}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {briefing.total_weight_kg && <p className="text-xs text-gray-500 mt-1 text-right">Total: {briefing.total_weight_kg} kg</p>}
          </div>
        )}

        {briefing.load_description && (
          <div className="mb-5">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Load Description</p>
            <p className="text-sm text-gray-700">{briefing.load_description}</p>
          </div>
        )}

        {/* Journey plan */}
        {briefing.journey_plan && (
          <div className="mb-5">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Journey Plan</p>
            <div className="space-y-0.5 text-xs font-mono">
              {(Array.isArray(briefing.journey_plan) ? briefing.journey_plan : briefing.journey_plan.steps || []).map((step: any, i: number) => (
                <div key={i} className={`flex items-center gap-3 py-0.5 ${
                  step.type === 'break' || step.type === 'rest' ? 'text-amber-700 font-bold bg-amber-50 rounded px-2 -mx-2' : 'text-gray-600'
                }`}>
                  <span className="w-12 text-right text-gray-400">{step.time_display || step.time || ''}</span>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    step.type === 'drive' ? 'bg-blue-400' : step.type === 'break' ? 'bg-amber-400' :
                    step.type === 'rest' ? 'bg-red-400' : step.type === 'wait' ? 'bg-gray-300' : 'bg-green-400'
                  }`} />
                  <span>{step.description || step.label || ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {briefing.notes && (
          <div className="border-t border-gray-200 pt-4 mb-6">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Notes</p>
            <pre className="text-sm text-gray-600 whitespace-pre-wrap font-sans">{briefing.notes}</pre>
          </div>
        )}

        {/* Sign-off */}
        <div className="border-t-2 border-gray-300 pt-6 mt-8 grid grid-cols-2 gap-8">
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-10">Driver Signature</p>
            <div className="border-b-2 border-gray-400" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-10">Date</p>
            <div className="border-b-2 border-gray-400" />
          </div>
        </div>
      </div>
    </div>
  );
}

function BriefField({ label, value, sub }: { label: string; value?: string | null; sub?: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-semibold text-gray-800">{value || '—'}</p>
      {sub && <p className="text-xs text-gray-500">{sub}</p>}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════
   JOURNEY TAB — route stops & journey plan
   ══════════════════════════════════════════════════════════════════════ */

function JourneyTab({ job }: { job: JobDetail }) {
  if (!job.stops && !job.journey_plan) {
    return (
      <div className="max-w-xl mx-auto text-center py-16">
        <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {journeyIcon}
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-1">No route data</h3>
        <p className="text-sm text-gray-500">Route and journey plan data is copied from the quote when the job is created.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Route summary bar */}
      <div className="bg-white rounded border border-gray-200 p-5 flex items-center gap-8">
        <StatBlock label="Total Distance" value={job.total_distance_miles ? `${job.total_distance_miles.toFixed(0)} miles` : '—'} />
        <StatBlock label="Driving Time" value={job.total_driving_hours ? `${job.total_driving_hours.toFixed(1)} hours` : '—'} />
        <StatBlock label="Total Duty" value={job.total_duty_hours ? `${job.total_duty_hours.toFixed(1)} hours` : '—'} />
        {(job.overnight_count ?? 0) > 0 && <StatBlock label="Overnights" value={String(job.overnight_count)} />}
      </div>

      {/* Stops */}
      {job.stops && job.stops.length > 0 && (
        <div className="bg-white rounded border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Route Stops</h3>
          <div className="space-y-0">
            {job.stops.map((s: any, i: number) => (
              <div key={i} className="flex items-start gap-4 relative">
                {/* Timeline line */}
                {i < job.stops!.length - 1 && (
                  <div className="absolute left-[13px] top-8 w-0.5 h-full bg-gray-200" />
                )}
                {/* Dot */}
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 z-10 ${
                  i === 0 ? 'bg-green-500 text-white' :
                  i === job.stops!.length - 1 ? 'bg-red-500 text-white' :
                  'bg-slate-100 text-blue-700'
                }`}>
                  {i === 0 ? 'A' : i === job.stops!.length - 1 ? 'B' : i}
                </div>
                <div className="pb-6">
                  <p className="text-sm font-medium text-gray-800">{s.label || s.postcode}</p>
                  {s.address && <p className="text-xs text-gray-500">{s.address}</p>}
                  {s.waiting_time_hours > 0 && (
                    <p className="text-xs text-amber-600 mt-0.5">{s.waiting_time_hours}h waiting</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Journey plan */}
      {job.journey_plan && (
        <div className="bg-white rounded border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Journey Plan (EC 561 / WTD Compliant)</h3>
          <div className="space-y-0.5">
            {(Array.isArray(job.journey_plan) ? job.journey_plan : job.journey_plan.steps || []).map((step: any, i: number) => (
              <div key={i} className={`flex items-center gap-3 py-1.5 px-3 rounded text-sm ${
                step.type === 'break' ? 'bg-amber-50 text-amber-800' :
                step.type === 'rest' ? 'bg-red-50 text-red-800' :
                step.type === 'wait' ? 'bg-gray-50 text-gray-600' :
                'text-gray-700'
              }`}>
                <span className="w-14 text-right font-mono text-xs text-gray-400">{step.time_display || step.time || ''}</span>
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                  step.type === 'drive' ? 'bg-blue-400' : step.type === 'break' ? 'bg-amber-400' :
                  step.type === 'rest' ? 'bg-red-400' : step.type === 'wait' ? 'bg-gray-300' : 'bg-green-400'
                }`} />
                <span className="flex-1">{step.description || step.label || ''}</span>
                {step.duration_display && <span className="text-xs text-gray-400">{step.duration_display}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════
   ACTIVITY TAB — amendment history and audit trail
   ══════════════════════════════════════════════════════════════════════ */

function ActivityTab({ job }: { job: JobDetail }) {
  // Mock activity data — in production this would come from backend audit trail
  const mockActivity = [
    {
      timestamp: new Date(job.created_at || new Date()).toISOString(),
      user: 'System',
      action: `Job created from quote`,
    },
    ...(job.assigned_at ? [{
      timestamp: job.assigned_at,
      user: 'System',
      action: `Assigned vehicle ${job.vehicle_reg} and driver ${job.driver_name}`,
    }] : []),
    ...(job.dispatch_date ? [{
      timestamp: job.dispatch_date,
      user: 'System',
      action: 'Status changed to Dispatched',
    }] : []),
    ...(job.completed_at ? [{
      timestamp: job.completed_at,
      user: 'System',
      action: 'Status changed to Completed',
    }] : []),
    ...(job.invoiced_at ? [{
      timestamp: job.invoiced_at,
      user: 'System',
      action: 'Status changed to Invoiced',
    }] : []),
  ].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  if (mockActivity.length === 0) {
    return (
      <div className="max-w-xl mx-auto text-center py-16">
        <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-1">No activity yet</h3>
        <p className="text-sm text-gray-500">Changes to this job will appear here.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-6">Activity Timeline</h3>
        <div className="space-y-0">
          {mockActivity.map((item, i) => {
            const date = new Date(item.timestamp);
            const timeStr = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
            const dateStr = date.toLocaleDateString('en-GB');

            return (
              <div key={i} className="flex items-start gap-4 relative">
                {/* Timeline line */}
                {i < mockActivity.length - 1 && (
                  <div className="absolute left-[13px] top-8 w-0.5 h-full bg-gray-200" />
                )}
                {/* Dot */}
                <div className="w-7 h-7 rounded-full bg-slate-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 z-10 ring-4 ring-white">
                  •
                </div>
                <div className="pb-6 flex-1">
                  <div className="flex items-baseline gap-2">
                    <p className="text-sm font-semibold text-gray-800">{item.action}</p>
                    <span className="text-xs text-gray-400">by {item.user}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {dateStr} at {timeStr}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Note about audit trail */}
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
        <p className="font-medium mb-1">About this activity log:</p>
        <p>Currently showing key status changes. Full audit trail with all field modifications will be available when backend audit logging is enabled.</p>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════
   SHARED UI PRIMITIVES
   ══════════════════════════════════════════════════════════════════════ */

function PromptCard({ color, title, desc, action, onClick }: {
  color: string; title: string; desc: string; action: string; onClick: () => void;
}) {
  const bgMap: Record<string, string> = { amber: 'bg-amber-50 border-amber-200', indigo: 'bg-indigo-50 border-indigo-200', purple: 'bg-purple-50 border-purple-200' };
  const btnMap: Record<string, string> = { amber: 'bg-amber-500 hover:bg-amber-600', indigo: 'bg-indigo-500 hover:bg-indigo-600', purple: 'bg-purple-500 hover:bg-purple-600' };
  const textMap: Record<string, string> = { amber: 'text-amber-800', indigo: 'text-indigo-800', purple: 'text-purple-800' };
  return (
    <div className={`rounded border-2 p-4 flex items-center justify-between ${bgMap[color]}`}>
      <div>
        <h3 className={`text-sm font-semibold ${textMap[color]}`}>{title}</h3>
        <p className={`text-xs mt-0.5 ${textMap[color]} opacity-75`}>{desc}</p>
      </div>
      <button onClick={onClick} className={`px-4 py-2 text-white text-sm font-semibold rounded ${btnMap[color]} transition flex-shrink-0`}>
        {action}
      </button>
    </div>
  );
}

function SummaryCard({ icon, title, items, onClick }: {
  icon: ReactNode; title: string; onClick?: () => void;
  items: { label: string; value: string; warn?: boolean }[];
}) {
  return (
    <div className={`bg-white rounded border border-gray-200 p-5 ${onClick ? 'cursor-pointer hover:shadow transition' : ''}`} onClick={onClick}>
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">{icon}</svg>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</h3>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex justify-between items-center">
            <span className="text-xs text-gray-400">{it.label}</span>
            <span className={`text-sm font-medium ${it.warn ? 'text-amber-500 italic' : 'text-gray-800'}`}>{it.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FormCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="bg-white rounded border border-gray-200 p-5">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{title}</h3>
      {children}
    </div>
  );
}

function StatBlock({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-semibold text-gray-800">{value}</p>
    </div>
  );
}

function Inp({ label, value, onChange, type = 'text', disabled = false, className = '', placeholder = '' }: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; disabled?: boolean; className?: string; placeholder?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        disabled={disabled} placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400 disabled:bg-gray-50 disabled:text-gray-500 transition" />
    </div>
  );
}
